/* eslint-disable @typescript-eslint/no-explicit-any */
// Design v12 (TaskCool 12.0, ADR 0020), faithful port of the reference additions (logic.js 12.0):
// - Bookmarks (isBm / toggleBm / liveBookmarks): personal per user. The reference keeps them in localStorage; here the
//   bookmark is the task's `bm` field, which the sync layer turns into setTaskBookmark operations (never a toggle).
// - nextAfter / closeAndAdvance: after Space, Trash or Enter in a title the next task in the column is selected.
// - platformVals: the version label shows the platform (the web app is WEB).
// - fitRows: a closed row whose title wraps onto two lines although it is short steps down a fit level.
// - cardOutListener: a click anywhere in a column outside its open task closes it (keeps the clicked row in place).
// - projFloat listeners: ☰ on the Projects column stuck at its edge opens it as a floating panel.
// - GIF pack (gifPlay): GIFs are muted looping videos with a still poster; in the picker they play on hover, in
//   comments only while ≥ 98 % visible.
import type { Pane, Project, Task, TaskList } from '@/domain/model';
import { t as tr } from '@/app/i18n';

type C = any;

/** The signed-in user's bookmark of a live or trashed task (epoch ms) or 0. */
export function bmOf(c: C, id: string): number {
  const h = c.findTask(id); if (h && !h.isProj) return h.t.bm || 0;
  for (const p of c.state.projects as Project[]) { const e = (p.trash || []).find(x => x.task && x.task.id === id); if (e) return e.task.bm || 0; }
  return 0;
}

/** Bookmarks the task, or removes its bookmark (reference toggleBm); trashed tasks can lose their bookmark too. */
export function toggleBm(c: C, id: string): void {
  const on = !!bmOf(c, id), set = (x: Task) => { if (on) delete x.bm; else x.bm = Date.now(); };
  const h = c.findTask(id);
  if (h && !h.isProj) c.edit((ps: Project[]) => { const r = c.locate(ps, id); if (r && !r.isProj) set(r.t); }, false, id);
  else c.edit((ps: Project[]) => { for (const p of ps) { const e = (p.trash || []).find(x => x.task && x.task.id === id); if (e) { set(e.task); return; } } }, false);
  c.flash(tr(on ? 'Bookmark removed' : 'Bookmarked'));
}

export interface BmRow { p: Project; l: Pick<TaskList, 'title'>; t: Task; trashed: boolean }

/** Bookmarked tasks the user can still see, newest bookmark first (reference liveBookmarks); memoized per data. */
export function liveBookmarks(c: C): BmRow[] {
  const m = c._bmMemo; if (m && m.ps === c.state.projects && m.me === c.state.me) return m.v;
  const out: Array<BmRow & { at: number }> = [], LIM = 30 * 864e5, now = Date.now();
  for (const p of c.state.projects as Project[]) {
    if (!c.canSee(p)) continue;
    for (const l of p.lists) for (const t of l.tasks) if (t.bm) out.push({ p, l, t, trashed: false, at: t.bm });
    for (const e of p.trash || []) if (e.task && e.task.bm && now - e.at < LIM) out.push({ p, l: { title: e.listTitle || '' }, t: e.task, trashed: true, at: e.task.bm });
  }
  out.sort((a, b) => b.at - a.at);
  c._bmMemo = { ps: c.state.projects, me: c.state.me, v: out };
  return out;
}

/** The task to select after `ids` leave the column: the next one below, else the one above (reference nextAfter). */
export function nextAfter(c: C, ids: string[], paneId?: string | null): string | null {
  const ords: Record<string, string[]> = c._ord || {};
  const pid = paneId && ords[paneId] && ords[paneId]!.some(x => ids.includes(x)) ? paneId : Object.keys(ords).find(k => (ords[k] || []).some(x => ids.includes(x)));
  const ord = (pid && ords[pid]) || []; if (!ord.length) return null;
  const idx = ids.map(id => ord.indexOf(id)).filter(i => i >= 0); if (!idx.length) return null;
  const hi = Math.max(...idx), lo = Math.min(...idx);
  return ord.slice(hi + 1).find(x => !ids.includes(x)) || ord.slice(0, lo).reverse().find(x => !ids.includes(x)) || null;
}

/**
 * Enter in a title: the task closes and the next task of the same list is selected; after the list's last task the
 * cursor moves to that list's "+ New task" (reference closeAndAdvance).
 */
export function closeAndAdvance(c: C, paneId: string, taskId: string): void {
  const ord: string[] = ((c._ord || {})[paneId]) || [], i = ord.indexOf(taskId), nxId = i > -1 ? ord[i + 1] : null;
  const cur = c.findTask(taskId), nx = nxId && c.findTask(nxId);
  const clr = (st: any) => { const o = { ...(st.bgSel || {}) }; delete o[paneId]; return { bgSel: o, sel: st.sel && st.sel.paneId === paneId ? null : st.sel }; };
  if (nx && cur && nx.l.id === cur.l.id) { c.setState((st: any) => ({ ...clr(st), picked: nxId, anchor: nxId })); return; }
  c.setState((st: any) => ({ ...clr(st), picked: null }));
  const lid = cur && cur.l && cur.l.id; if (!lid) return;
  let n = 0; const go = () => { const root = (c.rootRef && c.rootRef.current) || document, inp = root.querySelector('[data-pane-id="' + paneId + '"] input[data-new-task="' + lid + '"]') as HTMLInputElement | null;
    if (inp && inp.offsetParent !== null) { inp.focus(); return; } if (n++ < 20) requestAnimationFrame(go); }; requestAnimationFrame(go);
}

/** The version label's platform badge (reference platformVals); this client is the web app. */
export function platformVals(): { platform: string; platformName: string } { return { platform: 'WEB', platformName: tr('Web') }; }

/**
 * A closed row whose title wraps although it takes less than 60 % of the row steps down one fit level (at most two),
 * so its icons give the title room; any column width or density change starts over (reference fitRows).
 */
export function fitRows(c: C): void {
  const root: ParentNode = (c.rootRef && c.rootRef.current) || document;
  const sig = [...root.querySelectorAll<HTMLElement>('[data-pane-id]')].map(p => p.clientWidth).join(',') + '|' + (c.state.density || '');
  if (sig !== c._fitSig) { c._fitSig = sig; if (c._fitDown && Object.keys(c._fitDown).length) { c._fitDown = {}; c.setState({ fitTick: (c.state.fitTick || 0) + 1 }); return; } }
  let ch = false; c._fitDown = c._fitDown || {};
  root.querySelectorAll<HTMLElement>('[data-task-row]').forEach(row => { const tt = row.querySelector<HTMLElement>('[data-rt]'); if (!tt) return; const id = row.getAttribute('data-task-id'); if (!id) return;
    const cs = getComputedStyle(tt), lh = parseFloat(cs.lineHeight) || 21; if (tt.getBoundingClientRect().height < lh * 1.5) return;
    const cur = c._fitDown[id] || 0; if (cur >= 2) return;
    const txt = (tt.textContent || '').trim(), rw = row.getBoundingClientRect().width; if (c.tw(txt, cs.fontSize) > rw * 0.6) return;
    c._fitDown[id] = cur + 1; ch = true; });
  if (ch) c.setState({ fitTick: (c.state.fitTick || 0) + 1 });
}

/**
 * Document listeners of 12.0: a click in a column outside its open task closes it (the clicked row keeps its place),
 * and the floating Projects panel closes on a click outside it or Esc (reference _cardOut / _projFloatOut / _projFloatEsc).
 */
export function v12Listeners(c: C): () => void {
  const projOut = (e: MouseEvent) => { if (!c.state.projFloat) return; const tg = e.target as HTMLElement; const pe = tg.closest && tg.closest('[data-pane-id]'), pp = (c.state.panes as Pane[]).find(p => p.type === 'projects');
    if (pe && pp && pe.getAttribute('data-pane-id') === pp.id) return; if (tg.closest && tg.closest('[data-sheet], [data-ctx], [data-dsk-theme]')) return; c.setState({ projFloat: false }); };
  const projEsc = (e: KeyboardEvent) => { if (e.key === 'Escape' && c.state.projFloat) c.setState({ projFloat: false }); };
  const cardOut = (e: MouseEvent) => {
    if (e.button !== 0) return; const t = e.target as HTMLElement; if (!t || !t.closest) return;
    const pe = t.closest('[data-pane-id]') as HTMLElement | null; if (!pe) return; const pid = pe.getAttribute('data-pane-id')!;
    if (t.closest('[data-task-card], [data-tagpop], [data-tools-pop], [data-emoji-pop], [data-ctx], [data-sheet], [data-ctbmenu], [data-dsk-theme], input[type="file"]')) return;
    const sl = c.state.sel, bg = (c.state.bgSel || {})[pid], pn = (c.state.panes as Pane[]).find(p => p.id === pid);
    if (pn && pn.onlyTask) return;
    const tid = sl && sl.paneId === pid ? sl.taskId : bg && bg.taskId ? bg.taskId : null; if (!tid) return;
    const anc = (t.closest('[data-task-row], input[data-new-task], [data-list-title]') as HTMLElement | null) || t;
    const key = anc.getAttribute('data-task-id') ? '[data-task-row][data-task-id="' + anc.getAttribute('data-task-id') + '"]' : anc.getAttribute('data-new-task') ? 'input[data-new-task="' + anc.getAttribute('data-new-task') + '"]' : null;
    const y0 = anc.getBoundingClientRect().top;
    let sc: HTMLElement | null = anc.parentElement; while (sc && sc !== pe.parentElement) { const oy = getComputedStyle(sc).overflowY; if ((oy === 'auto' || oy === 'scroll') && sc.scrollHeight > sc.clientHeight) break; sc = sc.parentElement; }
    if (sc === pe.parentElement) sc = pe;
    const fix = () => { const el = key ? pe.querySelector(key) : (anc.isConnected ? anc : null); if (!el || !sc) return; const d = el.getBoundingClientRect().top - y0; if (Math.abs(d) > 0.5) sc.scrollTop += d; };
    c.setState((st: any) => { const o = { ...(st.bgSel || {}) }; delete o[pid]; return { bgSel: o, sel: st.sel && st.sel.paneId === pid ? null : st.sel, picked: st.picked || tid }; }, fix);
  };
  document.addEventListener('mousedown', projOut, true); document.addEventListener('keydown', projEsc); document.addEventListener('mousedown', cardOut, true);
  return () => { document.removeEventListener('mousedown', projOut, true); document.removeEventListener('keydown', projEsc); document.removeEventListener('mousedown', cardOut, true); };
}

/** componentDidUpdate (reference): rows are re-fitted after each render; the floating panel closes once unstuck. */
export function v12DidUpdate(c: C, prev?: any): void {
  cancelAnimationFrame(c._fitRaf); c._fitRaf = requestAnimationFrame(() => fitRows(c));
  if (c.state.projFloat && prev && (!c.state.projStuck || (prev.panes || []).length !== c.state.panes.length)) c.setState({ projFloat: false });
}

/** Deadline sort (reference 12.0): nearest deadline first, tasks without a deadline last, then the given order. */
export function byDeadline<T>(dl: (x: T) => string | undefined, tie: (x: T, y: T) => number): (x: T, y: T) => number {
  return (x, y) => { const a = dl(x) || '', b = dl(y) || ''; return (a ? 0 : 1) - (b ? 0 : 1) || (a < b ? -1 : a > b ? 1 : 0) || tie(x, y); };
}

// ---- GIF pack ----
export interface GifItem { id: string; src: string; poster: string }
/**
 * The built-in pack (ADR 0020): ids g01…g10, files public/gifs/gif-NN.mp4 + .png (supplied by the owner in the design
 * file "TaskCool v12 (1).html", 2026-10-21). Only entries listed in gifs/pack.json are offered (none = no GIF tab).
 * Loaded on demand; not precached by the service worker.
 */
export const GIF_IDS = ['g01', 'g02', 'g03', 'g04', 'g05', 'g06', 'g07', 'g08', 'g09', 'g10'];
const gifBase = (): string => (import.meta.env.BASE_URL || '/') + 'gifs/';
export const gifOf = (id: string): GifItem => { const n = id.slice(1); return { id, src: gifBase() + 'gif-' + n + '.mp4', poster: gifBase() + 'gif-' + n + '.png' }; };
let packCache: string[] | null = null, packLoad: Promise<string[]> | null = null;
/** Pack ids whose files ship with this build ([] until /gifs/pack.json exists). */
export function gifPack(onReady?: () => void): string[] {
  if (packCache) return packCache;
  if (!packLoad) packLoad = fetch(gifBase() + 'pack.json', { credentials: 'same-origin' }).then(r => (r.ok ? r.json() : [])).then((v: unknown) => (Array.isArray(v) ? v.filter(x => typeof x === 'string' && GIF_IDS.includes(x)) : [])).catch(() => [])
    .then(v => { packCache = v; if (onReady) onReady(); return v; });
  return [];
}

/**
 * Ref callback of a GIF <video> (reference gifPlay): muted, inline, poster first; `data-ghover` (picker) plays on
 * hover / tap, otherwise it plays only while at least 98 % of it is visible.
 */
export function gifPlay(c: C): (el: Element | null) => void {
  return c._gifPlay || (c._gifPlay = (el0: Element | null) => {
    const el = el0 as (HTMLVideoElement & { __gp?: number; __start?: () => void; __stop?: () => void }) | null; if (!el) return;
    el.muted = true; el.playsInline = true; el.setAttribute('playsinline', '');
    const pv = el.getAttribute('data-gposter'); if (pv && el.getAttribute('poster') !== pv) el.setAttribute('poster', pv);
    if (el.__gp) return; el.__gp = 1;
    const start = () => { const gs = el.getAttribute('data-gsrc'); if (!gs) return; if (el.getAttribute('src') !== gs) el.setAttribute('src', gs); const p = el.play && el.play(); if (p && p.catch) p.catch(() => undefined); };
    const stop = () => { try { el.pause(); } catch { /* not playing */ } };
    if (el.getAttribute('data-ghover')) { const b = (el.parentElement && el.parentElement.closest('button')) || el.parentElement; if (b) { b.addEventListener('mouseenter', start); b.addEventListener('mouseleave', stop); b.addEventListener('touchstart', () => { if (el.paused) start(); else stop(); }, { passive: true }); } return; }
    if (!('IntersectionObserver' in window)) { start(); return; }
    c._gifIO = c._gifIO || new IntersectionObserver(es => es.forEach(en => { const v = en.target as typeof el; if (en.isIntersecting && en.intersectionRatio >= 0.98) v!.__start && v!.__start(); else v!.__stop && v!.__stop(); }), { threshold: [0, 0.98, 1] });
    el.__start = start; el.__stop = stop; c._gifIO.observe(el);
  });
}
